import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Grid as SwiperGrid, Navigation, Pagination } from "swiper/modules";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import defaultImage from "../image/Image_not_available.png";
import "./styles.css";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/pagination";
import "swiper/css/navigation";

const baseImageBucketUrl = process.env.REACT_APP_S3_IMAGE_BUCKET_URL;

const getConstructionImage = (image) =>
  image ? `${baseImageBucketUrl}${image}` : defaultImage;

const ConstructionCard = ({ project, index }) => {
  const history = useHistory();
  const title = project.title || project.projectTitle || "Homely project";
  const projectDetails = [
    project.typeOfBuilding,
    project.area,
    project.location,
  ].filter(Boolean);

  return (
    <motion.article
      className="construction-project"
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
        delay: Math.min(index * 0.08, 0.24),
      }}
    >
      <button
        type="button"
        className="construction-project__image-button"
        onClick={() =>
          history.push(`/construction/${project.id}`, {
            id: project.id,
            image: project.projectImage,
            title,
            location: project.location,
            area: project.area,
            status: project.projectStatus,
            projectType: project.projectType,
            details: project.details,
            typeOfBuilding: project.typeOfBuilding,
          })
        }
        aria-label={`View ${title}`}
      >
        <img
          className="construction-project__image"
          src={getConstructionImage(project.projectImage)}
          alt={title}
          loading="lazy"
        />
        <span className="construction-project__image-shade" />
        {project.projectStatus && (
          <span className="construction-project__status">
            {project.projectStatus}
          </span>
        )}
        <span className="construction-project__view">
          View project <span aria-hidden="true">↗</span>
        </span>
      </button>
      <Box className="construction-project__caption">
        <Box>
          <Typography className="construction-project__eyebrow">
            {project.projectType || "Construction"}
          </Typography>
          <Typography component="h3" className="construction-project__title">
            {title}
          </Typography>
        </Box>
        {projectDetails.length > 0 && (
          <Typography className="construction-project__details">
            {projectDetails.join(" · ")}
          </Typography>
        )}
      </Box>
    </motion.article>
  );
};

const PortfolioSection = ({ data, onViewAll }) => (
  <Box component="section" className="construction-portfolio">
    <Box className="construction-portfolio__header">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Typography className="construction-portfolio__eyebrow">
          SELECTED WORK
        </Typography>
        <Typography component="h2" className="construction-portfolio__title">
          Built with care. Made to last.
        </Typography>
        <Typography className="construction-portfolio__intro">
          A collection of spaces shaped by thoughtful planning and lasting
          craftsmanship.
        </Typography>
      </motion.div>
      {onViewAll && (
        <Button
          className="construction-portfolio__all"
          onClick={onViewAll}
          endIcon={<span aria-hidden="true">↗</span>}
        >
          Explore all projects
        </Button>
      )}
    </Box>

    {data?.length > 0 ? (
      <Swiper
        className="construction-portfolio__swiper"
        spaceBetween={22}
        slidesPerView={1.12}
        grid={{ rows: 1, fill: "row" }}
        breakpoints={{
          640: { slidesPerView: 2, spaceBetween: 24 },
          1024: { slidesPerView: 3, spaceBetween: 28 },
        }}
        navigation={data.length > 1}
        pagination={data.length > 1 ? { clickable: true } : false}
        modules={[SwiperGrid, Navigation, Pagination]}
      >
        {data.map((project, index) => (
          <SwiperSlide
            key={project.id || `${project.title || "project"}-${index}`}
          >
            <ConstructionCard project={project} index={index} />
          </SwiperSlide>
        ))}
      </Swiper>
    ) : (
      <Typography className="construction-portfolio__empty">
        Project highlights will be shown here soon.
      </Typography>
    )}
  </Box>
);

export default PortfolioSection;
